import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { onContractService } from "../../services/onContractService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { PageLayout } from "../common/index.js"
import { ArrowLeft, FileText, Calculator } from "lucide-react"

// ─── Defined OUTSIDE the component so it never gets remounted on re-render ───
const ChargeInput = ({ label, name, hint, value, onChange }) => (
  <div>
    <label className="block text-sm font-medium text-secondary mb-1">{label}</label>
    {hint && <p className="text-xs text-gray-400 mb-1">{hint}</p>}
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">₹</span>
      <input
        type="text"
        inputMode="decimal"
        name={name}
        value={value}
        onChange={onChange}
        placeholder="0.00"
        className="w-full pl-7 pr-4 py-2.5 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
      />
    </div>
  </div>
)

const EditOnContract = () => {
  const { contractId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()

  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [loadingMLSheets, setLoadingMLSheets] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)

  const [contract, setContract] = useState(null)
  const [availableMLSheets, setAvailableMLSheets] = useState([])
  const [selectedMLSheetIds, setSelectedMLSheetIds] = useState([])
  const [preview, setPreview] = useState({ total_shipments: 0, total_revenue: 0 })

  const [formData, setFormData] = useState({
    start_date: "",
    end_date: "",
    contract_value: "",
    handling_charges: "",
    parking_charges: "",
    other_charges: "",
  })

  const tripPL =
    parseFloat(preview.total_revenue || 0) -
    parseFloat(formData.contract_value || 0) -
    parseFloat(formData.handling_charges || 0) -
    parseFloat(formData.parking_charges || 0) -
    parseFloat(formData.other_charges || 0)

  useEffect(() => {
    fetchContract()
  }, [contractId])

  useEffect(() => {
    if (selectedMLSheetIds.length > 0) {
      fetchPreview(selectedMLSheetIds)
    } else {
      setPreview({ total_shipments: 0, total_revenue: 0 })
    }
  }, [selectedMLSheetIds])

  const fetchContract = async () => {
    try {
      setLoading(true)
      const res = await onContractService.getOne(contractId)
      const c = res.data.contract

      setContract(c)
      setSelectedMLSheetIds(c.ml_sheet_ids || [])
      setFormData({
        start_date: c.start_date || "",
        end_date: c.end_date || "",
        contract_value: c.contract_value || "",
        handling_charges: c.handling_charges || "",
        parking_charges: c.parking_charges || "",
        other_charges: c.other_charges || "",
      })
      setPreview({
        total_shipments: c.total_shipments || 0,
        total_revenue: c.total_revenue || 0,
      })

      await fetchAvailableMLSheets(c.vehicle_id, c.ml_sheet_ids || [])
    } catch (err) {
      console.error("Error loading contract:", err)
      Alert.error("Failed to load contract")
      navigate("/on-contracts")
    } finally {
      setLoading(false)
    }
  }

  const fetchAvailableMLSheets = async (vehicleId, currentIds) => {
    try {
      setLoadingMLSheets(true)
      const res = await onContractService.getAvailableMLSheets(vehicleId)
      const available = res.data.available_ml_sheets || []

      const availableIds = available.map((ml) => ml.ml_sheet_id)
      const missingCurrent = currentIds.filter((id) => !availableIds.includes(id))

      const stubs = missingCurrent.map((id) => ({
        ml_sheet_id: id,
        status: "—",
        shipment_ids: [],
        from_locations: [],
        to_locations: [],
        created_at: null,
        _alreadyAssigned: true,
      }))

      setAvailableMLSheets([...available, ...stubs])
    } catch (err) {
      console.error("Error fetching available ML sheets:", err)
    } finally {
      setLoadingMLSheets(false)
    }
  }

  const fetchPreview = async (ids) => {
    try {
      setLoadingPreview(true)
      const res = await onContractService.getMLSheetData(ids)
      setPreview({
        total_shipments: res.data.total_shipments || 0,
        total_revenue: res.data.total_revenue || 0,
      })
    } catch (err) {
      console.error("Error fetching preview:", err)
    } finally {
      setLoadingPreview(false)
    }
  }

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value })
  }

  const handleMLSheetToggle = (mlSheetId) => {
    setSelectedMLSheetIds((prev) =>
      prev.includes(mlSheetId) ? prev.filter((id) => id !== mlSheetId) : [...prev, mlSheetId]
    )
  }

  const handleSelectAll = () => {
    if (selectedMLSheetIds.length === availableMLSheets.length) {
      setSelectedMLSheetIds([])
    } else {
      setSelectedMLSheetIds(availableMLSheets.map((ml) => ml.ml_sheet_id))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (selectedMLSheetIds.length === 0) return Alert.error("Select at least one ML Sheet")
    if (!formData.start_date || !formData.end_date) return Alert.error("Start and end dates are required")
    if (formData.start_date > formData.end_date) return Alert.error("End date must be after start date")

    try {
      setSubmitting(true)
      const payload = {
        start_date: formData.start_date,
        end_date: formData.end_date,
        ml_sheet_ids: selectedMLSheetIds,
        contract_value: parseFloat(formData.contract_value || 0),
        handling_charges: parseFloat(formData.handling_charges || 0),
        parking_charges: parseFloat(formData.parking_charges || 0),
        other_charges: parseFloat(formData.other_charges || 0),
      }
      await onContractService.update(contractId, payload)
      Alert.success("Contract updated successfully")
      navigate(`/on-contracts/view/${contractId}`)
    } catch (err) {
      console.error("Error updating contract:", err)
      Alert.error(err.response?.data?.error || "Failed to update contract")
    } finally {
      setSubmitting(false)
    }
  }

  if (loading) {
    return (
      <PageLayout title="Edit On-Contract" icon={FileText} maxWidth="max-w-7xl">
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4" />
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout title="Edit On-Contract" icon={FileText} maxWidth="max-w-7xl">
      <form onSubmit={handleSubmit} className="space-y-4">
                  {/* Contract Period */}
                  <div className="bg-white rounded-sm shadow p-5">
                    <h2 className="font-semibold text-secondary mb-4">Contract Period</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-secondary mb-1">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="start_date"
                          value={formData.start_date}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-secondary mb-1">
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          name="end_date"
                          value={formData.end_date}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2.5 border border-gray-300 rounded-sm focus:outline-none focus:border-brand text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* ML Sheet Selection */}
                  <div className="bg-white rounded-sm shadow p-5">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-semibold text-secondary flex items-center gap-2">
                        <FileText className="w-4 h-4 text-blue-500" />
                        ML Sheets ({availableMLSheets.length})
                        {loadingMLSheets && <span className="text-xs text-brand ml-2">Loading...</span>}
                      </h2>
                      {availableMLSheets.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAll}
                          className="px-3 py-1.5 bg-brand text-white text-xs font-semibold rounded-sm hover:bg-brand"
                        >
                          {selectedMLSheetIds.length === availableMLSheets.length ? "Deselect All" : "Select All"}
                        </button>
                      )}
                    </div>

                    {loadingMLSheets ? (
                      <div className="text-center py-8">
                        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-brand"></div>
                      </div>
                    ) : (
                      <div className="space-y-2 max-h-[350px] overflow-y-auto">
                        {availableMLSheets.map((ml) => {
                          const isSelected = selectedMLSheetIds.includes(ml.ml_sheet_id)
                          return (
                            <label
                              key={ml.ml_sheet_id}
                              className={`flex items-start p-3 border rounded-sm cursor-pointer transition-colors ${
                                isSelected ? "border-blue-400 bg-blue-50" : "border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => handleMLSheetToggle(ml.ml_sheet_id)}
                                className="mt-0.5 w-4 h-4 flex-shrink-0"
                              />
                              <div className="ml-3 flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="font-bold text-blue-600 text-sm">{ml.ml_sheet_id}</span>
                                  <span
                                    className={`text-xs px-2 py-0.5 rounded border font-semibold ${
                                      ml._alreadyAssigned
                                        ? "border-blue-400 bg-brand-light text-brand"
                                        : ml.status === "OPEN" || ml.status === "open"
                                        ? "border-green-400 bg-green-50 text-green-700"
                                        : "border-gray-300 bg-gray-50 text-gray-700"
                                    }`}
                                  >
                                    {ml._alreadyAssigned ? "This Contract" : ml.status}
                                  </span>
                                </div>
                                {!ml._alreadyAssigned && (
                                  <div className="text-xs text-gray-500 mt-1">
                                    {ml.shipment_ids?.length || 0} shipments
                                    {ml.from_locations?.length > 0 && (
                                      <span className="ml-2">
                                        · {ml.from_locations.map((l) => l.location || l.name).join(", ")} → {ml.to_locations?.map((l) => l.location || l.name).join(", ")}
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </label>
                          )
                        })}
                      </div>
                    )}

                    {selectedMLSheetIds.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-gray-100 flex flex-wrap gap-4 text-sm">
                        <div>
                          <span className="text-gray-500">Selected: </span>
                          <span className="font-bold text-blue-600">{selectedMLSheetIds.length}</span>
                        </div>
                        {loadingPreview ? (
                          <div className="text-xs text-brand">Calculating...</div>
                        ) : (
                          <>
                            <div>
                              <span className="text-gray-500">Shipments: </span>
                              <span className="font-bold">{preview.total_shipments}</span>
                            </div>
                            <div>
                              <span className="text-gray-500">Revenue: </span>
                              <span className="font-bold text-green-600">
                                ₹{parseFloat(preview.total_revenue).toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Charges */}
                  <div className="bg-white rounded-sm shadow p-5">
                    <h2 className="font-semibold text-secondary mb-4 flex items-center gap-2">
                      <Calculator className="w-4 h-4 text-blue-500" /> Charges
                    </h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <ChargeInput
                        label="Contract Value *"
                        name="contract_value"
                        hint="Agreed trip payment to transporter"
                        value={formData.contract_value}
                        onChange={handleChange}
                      />
                      <ChargeInput
                        label="Handling Charges"
                        name="handling_charges"
                        value={formData.handling_charges}
                        onChange={handleChange}
                      />
                      <ChargeInput
                        label="Parking Charges"
                        name="parking_charges"
                        value={formData.parking_charges}
                        onChange={handleChange}
                      />
                      <ChargeInput
                        label="Other Charges"
                        name="other_charges"
                        value={formData.other_charges}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  {/* P&L Preview */}
                  <div className="bg-white rounded-sm shadow p-5">
                    <h2 className="font-semibold text-secondary mb-4">Trip P&L Preview</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                      {[
                        { label: "Total Revenue", value: preview.total_revenue, color: "text-green-600" },
                        { label: "Contract Value", value: formData.contract_value || 0, color: "text-gray-700" },
                        { label: "Handling", value: formData.handling_charges || 0, color: "text-gray-700" },
                        { label: "Parking", value: formData.parking_charges || 0, color: "text-gray-700" },
                        { label: "Other", value: formData.other_charges || 0, color: "text-gray-700" },
                        { label: "Shipments", value: preview.total_shipments, color: "text-brand", isCount: true },
                      ].map((item) => (
                        <div key={item.label} className="bg-gray-50 rounded-sm p-3">
                          <div className="text-xs text-gray-500">{item.label}</div>
                          <div className={`font-bold text-sm mt-1 ${item.color}`}>
                            {item.isCount
                              ? item.value
                              : `₹${parseFloat(item.value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div
                      className={`flex items-center justify-between p-4 rounded-sm border-2 ${
                        tripPL >= 0 ? "bg-green-50 border-green-400" : "bg-red-50 border-red-400"
                      }`}
                    >
                      <span className="font-bold text-base text-secondary">Trip P&L</span>
                      <span className={`font-bold text-xl ${tripPL >= 0 ? "text-green-600" : "text-red-600"}`}>
                        {tripPL >= 0 ? "+" : ""}₹{tripPL.toLocaleString("en-IN", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex justify-between pb-6">
                    <button
                      type="button"
                      onClick={() => navigate(`/on-contracts/view/${contractId}`)}
                      className="px-5 py-2.5 border border-gray-300 text-gray-600 rounded-sm hover:bg-gray-50 font-semibold text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={submitting}
                      className="px-6 py-2.5 bg-blue-600 text-white rounded-sm hover:bg-blue-700 font-semibold text-sm disabled:opacity-60"
                    >
                      {submitting ? "Saving..." : "Save Changes"}
                    </button>
                  </div>
      </form>
    </PageLayout>
  )
}

export default EditOnContract